import { formatArtifactMaxFileSize, getArtifactMaxFileSizeBytes } from "../framework/artifacts.js";
/**
 * How many files one message may carry.
 *
 * A bound on *registration writes per message*, not on bytes: each file attached here becomes a
 * delivery row, an artifact row and a relation inside one transaction.
 */
export const CLAUDIA_ATTACHMENT_MAX_PER_PROMPT = 5;
/**
 * The bytes one message may put in front of the model.
 *
 * Only an image spends this budget, because only an image's bytes enter the prompt: a document
 * attached in chat is registered as a project artifact, parsed by the pipeline and read back
 * through the document tools, so none of its bytes are ever sent. Per-file size is not this
 * constant's business either — that is `getArtifactMaxFileSizeBytes`, the one ceiling the
 * Documents screen and the composer both take.
 *
 * `MODEL_IMAGE_VARIANT_MAX_BYTES` times `CLAUDIA_ATTACHMENT_MAX_PER_PROMPT` already sits far
 * under this. The budget bounds the total independently so that raising either of those cannot
 * silently raise what one message sends.
 */
export const CLAUDIA_ATTACHMENT_MAX_MODEL_BYTES_PER_PROMPT = 25 * 1024 * 1024;
export const CLAUDIA_ATTACHMENT_MAX_DRAFTS_PER_USER_THREAD = 15;
export const CLAUDIA_ATTACHMENT_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
/**
 * How many *existing* project documents one call may place into a conversation
 * (LAT-870 D1, `claudia.attachArtifactsToThread`).
 *
 * Deliberately not one of the caps above: those bound what is delivered to the model in a
 * single prompt (bytes, files, MIME types through the attachment gateway). This one bounds
 * a *placement* — relation rows written in one mutation, which the model then reaches
 * through its document tools like any other project file. Two different limits because they
 * protect two different things: a transaction's write budget here, a context window there.
 */
export const CLAUDIA_THREAD_ARTIFACT_ATTACH_MAX_PER_CALL = 25;
export const MODEL_IMAGE_MAX_EDGE = 1568;
export const MODEL_IMAGE_VARIANT_MAX_BYTES = 192 * 1024;
/**
 * The same ceiling for a variant of a *rendered view* — a page or a worksheet the document
 * processor drew rather than a picture the document carried.
 *
 * A view earns its place by being read: the file names down a dense sheet tile, the labels inside
 * the boxes of a diagram. `MODEL_IMAGE_MAX_EDGE` is what that reading needs, so a view is encoded
 * at that edge and the encoder's quality is what gives way; held to 192 KB it would instead be
 * shrunk, and a tile sent smaller transcribes names it can no longer read. What makes the larger
 * number affordable is how few of them a document has: renders are capped per document at
 * `PAGE_RENDER_MAX_COUNT_PACKAGED`, where pictures run to a thousand.
 */
export const MODEL_IMAGE_VARIANT_RENDERED_VIEW_MAX_BYTES = 512 * 1024;
export const ELEMENT_GET_IMAGE_MAX_COUNT = 3;
export const ELEMENT_GET_MEDIA_MAX_TOTAL_BYTES = 576 * 1024;
export const CLAUDIA_ATTACHMENT_FILE_TYPE_REGISTRY = [
    { extensions: ["png"], canonicalMimeType: "image/png", category: "image", enabled: true },
    {
        extensions: ["jpg", "jpeg"],
        canonicalMimeType: "image/jpeg",
        category: "image",
        enabled: true,
    },
    { extensions: ["webp"], canonicalMimeType: "image/webp", category: "image", enabled: true },
    { extensions: ["gif"], canonicalMimeType: "image/gif", category: "image", enabled: true },
    { extensions: ["pdf"], canonicalMimeType: "application/pdf", category: "pdf", enabled: true },
    {
        extensions: ["docx"],
        canonicalMimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        category: "document",
        enabled: true,
    },
    {
        extensions: ["doc"],
        canonicalMimeType: "application/msword",
        category: "document",
        enabled: true,
    },
    {
        extensions: ["odt"],
        canonicalMimeType: "application/vnd.oasis.opendocument.text",
        category: "document",
        enabled: true,
    },
    {
        extensions: ["rtf"],
        canonicalMimeType: "application/rtf",
        mimeAliases: ["text/rtf"],
        category: "document",
        enabled: true,
    },
    {
        extensions: ["pptx"],
        canonicalMimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        category: "presentation",
        enabled: true,
    },
    {
        extensions: ["ppt"],
        canonicalMimeType: "application/vnd.ms-powerpoint",
        category: "presentation",
        enabled: true,
    },
    {
        extensions: ["xlsx"],
        canonicalMimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        category: "spreadsheet",
        enabled: true,
    },
    {
        extensions: ["xls"],
        canonicalMimeType: "application/vnd.ms-excel",
        category: "spreadsheet",
        enabled: true,
    },
    {
        extensions: ["csv"],
        canonicalMimeType: "text/csv",
        mimeAliases: ["application/csv", "application/vnd.ms-excel", "text/plain"],
        category: "spreadsheet",
        enabled: true,
    },
    {
        extensions: ["tsv"],
        canonicalMimeType: "text/tsv",
        mimeAliases: ["text/tab-separated-values", "text/plain"],
        category: "spreadsheet",
        enabled: false,
    },
    {
        extensions: [
            "txt",
            "log",
            "conf",
            "py",
            "java",
            "c",
            "h",
            "cpp",
            "hpp",
            "cs",
            "go",
            "rb",
            "php",
            "sh",
            "sql",
        ],
        canonicalMimeType: "text/plain",
        category: "text",
        enabled: true,
    },
    {
        extensions: ["md", "markdown"],
        canonicalMimeType: "text/markdown",
        mimeAliases: ["text/plain"],
        category: "text",
        enabled: true,
    },
    {
        extensions: ["json"],
        canonicalMimeType: "application/json",
        mimeAliases: ["text/json", "text/plain"],
        category: "text",
        enabled: false,
    },
    {
        extensions: ["xml"],
        canonicalMimeType: "application/xml",
        mimeAliases: ["text/xml", "text/plain"],
        category: "text",
        enabled: false,
    },
    {
        extensions: ["yaml", "yml"],
        canonicalMimeType: "application/x-yaml",
        mimeAliases: ["text/yaml", "text/x-yaml", "application/yaml", "text/plain"],
        category: "text",
        enabled: false,
    },
    {
        extensions: ["toml"],
        canonicalMimeType: "application/toml",
        mimeAliases: ["text/plain"],
        category: "text",
        enabled: false,
    },
    {
        extensions: ["html"],
        canonicalMimeType: "text/html",
        mimeAliases: ["text/plain"],
        category: "text",
        enabled: false,
    },
    {
        extensions: ["css"],
        canonicalMimeType: "text/css",
        mimeAliases: ["text/plain"],
        category: "text",
        enabled: false,
    },
    {
        extensions: ["js", "jsx"],
        canonicalMimeType: "text/javascript",
        mimeAliases: ["application/javascript", "application/x-javascript", "text/plain"],
        category: "text",
        enabled: false,
    },
    {
        extensions: ["ts", "tsx"],
        canonicalMimeType: "text/typescript",
        mimeAliases: ["application/typescript", "text/plain"],
        category: "text",
        enabled: false,
    },
    // Recordings. Every extension `classifyArtifactFile` calls audio or video has an entry, and
    // the two lists are pinned against each other by `tests/claudiaAttachmentRegistry.test.ts`:
    // a type the classifier routes to media storage but the registry refuses would be an upload
    // the composer accepts and the gateway cannot place. `ogg` precedes `opus` so the shared
    // `audio/ogg` resolves to the container rather than to one of its codecs.
    {
        extensions: ["mp3", "mpga", "mpeg"],
        canonicalMimeType: "audio/mpeg",
        mimeAliases: ["audio/mp3", "audio/mpeg3", "audio/x-mpeg-3"],
        category: "audio",
        enabled: true,
    },
    {
        extensions: ["m4a"],
        canonicalMimeType: "audio/mp4",
        mimeAliases: ["audio/x-m4a", "audio/m4a"],
        category: "audio",
        enabled: true,
    },
    {
        extensions: ["wav"],
        canonicalMimeType: "audio/wav",
        mimeAliases: ["audio/x-wav", "audio/wave", "audio/vnd.wave"],
        category: "audio",
        enabled: true,
    },
    { extensions: ["aac"], canonicalMimeType: "audio/aac", category: "audio", enabled: true },
    {
        extensions: ["flac"],
        canonicalMimeType: "audio/flac",
        mimeAliases: ["audio/x-flac"],
        category: "audio",
        enabled: true,
    },
    { extensions: ["ogg", "oga"], canonicalMimeType: "audio/ogg", category: "audio", enabled: true },
    {
        extensions: ["opus"],
        canonicalMimeType: "audio/opus",
        mimeAliases: ["audio/ogg"],
        category: "audio",
        enabled: true,
    },
    { extensions: ["mp4"], canonicalMimeType: "video/mp4", category: "video", enabled: true },
    {
        extensions: ["mov"],
        canonicalMimeType: "video/quicktime",
        category: "video",
        enabled: true,
    },
    { extensions: ["webm"], canonicalMimeType: "video/webm", category: "video", enabled: true },
    {
        extensions: ["mkv"],
        canonicalMimeType: "video/x-matroska",
        category: "video",
        enabled: true,
    },
    {
        extensions: ["avi"],
        canonicalMimeType: "video/x-msvideo",
        mimeAliases: ["video/avi", "video/msvideo"],
        category: "video",
        enabled: true,
    },
];
const CLAUDIA_ATTACHMENT_ENABLED_FILE_TYPES = CLAUDIA_ATTACHMENT_FILE_TYPE_REGISTRY.filter((entry) => entry.enabled);
export const CLAUDIA_ATTACHMENT_IMAGE_MIME_TYPES = CLAUDIA_ATTACHMENT_ENABLED_FILE_TYPES.filter((entry) => entry.category === "image").map((entry) => entry.canonicalMimeType);
export const CLAUDIA_ATTACHMENT_IMAGE_EXTENSIONS = CLAUDIA_ATTACHMENT_ENABLED_FILE_TYPES.filter((entry) => entry.category === "image").flatMap((entry) => entry.extensions);
/**
 * File-part media types the model transport (gateway) is proven to accept. Used at the context
 * boundary to keep unsupported file parts out of model input (e.g. rows persisted while a
 * later-disabled type was still enabled).
 */
export const CLAUDIA_ATTACHMENT_MODEL_FILE_MIME_TYPES = ["application/pdf"];
export function isClaudiaAttachmentModelSupportedFileMime(mimeType) {
    const normalized = normalizeMimeType(mimeType);
    return CLAUDIA_ATTACHMENT_MODEL_FILE_MIME_TYPES.includes(normalized);
}
export const CLAUDIA_ATTACHMENT_ALLOWED_MIME_TYPES = CLAUDIA_ATTACHMENT_ENABLED_FILE_TYPES.flatMap((entry) => [entry.canonicalMimeType, ...(entry.mimeAliases ?? [])]);
const CLAUDIA_ATTACHMENT_EXTENSION_MAP = new Map(CLAUDIA_ATTACHMENT_ENABLED_FILE_TYPES.flatMap((entry) => entry.extensions.map((extension) => [extension, entry])));
function normalizeMimeType(mimeType) {
    return (mimeType ?? "").split(";")[0]?.trim().toLowerCase() ?? "";
}
export function getClaudiaAttachmentFileExtension(fileName) {
    const trimmed = fileName.trim();
    if (!trimmed || !trimmed.includes("."))
        return null;
    return trimmed.split(".").pop()?.toLowerCase() ?? null;
}
export function getClaudiaAttachmentKindForCategory(category) {
    return category === "image" ? "image" : "document";
}
export function getClaudiaAttachmentFileTypeForExtension(extension) {
    if (!extension)
        return null;
    return CLAUDIA_ATTACHMENT_EXTENSION_MAP.get(extension.toLowerCase()) ?? null;
}
export function getClaudiaAttachmentFileTypeForMime(mimeType) {
    const normalized = normalizeMimeType(mimeType);
    if (!normalized || normalized === "application/octet-stream")
        return null;
    return (CLAUDIA_ATTACHMENT_ENABLED_FILE_TYPES.find((entry) => entry.canonicalMimeType === normalized || (entry.mimeAliases ?? []).includes(normalized)) ?? null);
}
export function isClaudiaAttachmentMimeAllowedForFileType(args) {
    const normalized = normalizeMimeType(args.mimeType);
    return (!normalized ||
        normalized === "application/octet-stream" ||
        args.fileType.canonicalMimeType === normalized ||
        (args.fileType.mimeAliases ?? []).includes(normalized));
}
export function resolveClaudiaAttachmentFileType(fileName, mimeType) {
    const extension = getClaudiaAttachmentFileExtension(fileName);
    const fileType = getClaudiaAttachmentFileTypeForExtension(extension);
    if (!extension || !fileType) {
        return null;
    }
    if (!isClaudiaAttachmentMimeAllowedForFileType({ fileType, mimeType })) {
        return null;
    }
    return {
        fileType,
        extension,
        canonicalMimeType: fileType.canonicalMimeType,
        category: fileType.category,
        kind: getClaudiaAttachmentKindForCategory(fileType.category),
    };
}
function buildAcceptAttribute(entries) {
    return [
        ...new Set(entries.flatMap((entry) => [
            ...entry.extensions.map((extension) => `.${extension}`),
            entry.canonicalMimeType,
            ...(entry.mimeAliases ?? []),
        ])),
    ].join(",");
}
export const CLAUDIA_ATTACHMENT_ACCEPT_ATTRIBUTE = buildAcceptAttribute(CLAUDIA_ATTACHMENT_ENABLED_FILE_TYPES);
export const CLAUDIA_ATTACHMENT_IMAGE_ACCEPT_ATTRIBUTE = buildAcceptAttribute(CLAUDIA_ATTACHMENT_ENABLED_FILE_TYPES.filter((entry) => entry.category === "image"));
/**
 * The word a person recognises each accepted category by.
 *
 * A `Record` over the category union, so a new category cannot be accepted for upload without
 * being given a name here. The extensions are the ones people name a file by; the registry
 * accepts more of them per category (fifteen for `text` alone), and listing all of those would
 * make the hint unreadable without telling anyone anything they did not already assume.
 */
const CLAUDIA_ATTACHMENT_CATEGORY_LABELS = {
    image: "PNG · JPG · WEBP · GIF",
    pdf: "PDF",
    document: "DOCX",
    presentation: "PPTX",
    spreadsheet: "XLSX · CSV",
    text: "TXT",
    audio: "MP3 · M4A · WAV",
    video: "MP4 · MOV · WEBM",
};
const CLAUDIA_ATTACHMENT_CATEGORY_FAMILIES = {
    image: "image",
    pdf: "document",
    document: "document",
    presentation: "document",
    spreadsheet: "document",
    text: "document",
    audio: "recording",
    video: "recording",
};
const CLAUDIA_ATTACHMENT_FAMILY_NOUNS = {
    image: "an image",
    document: "a document",
    recording: "a recording",
};
const CLAUDIA_ATTACHMENT_FAMILY_ORDER = [
    "image",
    "document",
    "recording",
];
/**
 * What a refused file is told the composer takes.
 *
 * Read from the registry rather than written out, so a type that becomes enabled or disabled
 * changes the sentence with it — the alternative is a literal that goes on naming the old
 * accept list after the next widening.
 */
export function describeClaudiaAttachmentSupportedTypes() {
    const labelsByFamily = new Map();
    for (const entry of CLAUDIA_ATTACHMENT_ENABLED_FILE_TYPES) {
        const family = CLAUDIA_ATTACHMENT_CATEGORY_FAMILIES[entry.category];
        const label = CLAUDIA_ATTACHMENT_CATEGORY_LABELS[entry.category];
        const labels = labelsByFamily.get(family) ?? [];
        if (!labels.includes(label))
            labels.push(label);
        labelsByFamily.set(family, labels);
    }
    const clauses = CLAUDIA_ATTACHMENT_FAMILY_ORDER.flatMap((family) => {
        const labels = labelsByFamily.get(family);
        if (!labels || labels.length === 0)
            return [];
        return [`${CLAUDIA_ATTACHMENT_FAMILY_NOUNS[family]} (${labels.join(" · ")})`];
    });
    if (clauses.length === 0)
        return "Attachments are not accepted here.";
    const last = clauses[clauses.length - 1];
    const leading = clauses.slice(0, -1);
    return leading.length === 0 ? `Attach ${last}.` : `Attach ${leading.join(", ")} or ${last}.`;
}
/**
 * What the composer's dropzone says it takes: the categories that are actually enabled, and the
 * ceiling each of them is measured against.
 *
 * Every part of the sentence is read from the registry and from
 * `getArtifactMaxFileSizeBytes` — the same function the upload itself refuses by — so a type
 * that is enabled or disabled, and a ceiling that moves, change the hint with it. Categories
 * are grouped by ceiling because that is the only distinction a person has to act on.
 */
export function describeClaudiaAttachmentDropHint() {
    const groups = new Map();
    for (const entry of CLAUDIA_ATTACHMENT_ENABLED_FILE_TYPES) {
        const label = CLAUDIA_ATTACHMENT_CATEGORY_LABELS[entry.category];
        const sampleFileName = `file.${entry.extensions[0]}`;
        const maxBytes = getArtifactMaxFileSizeBytes(sampleFileName, entry.canonicalMimeType);
        const group = groups.get(maxBytes) ?? {
            labels: [],
            sampleFileName,
            sampleMime: entry.canonicalMimeType,
        };
        if (!group.labels.includes(label))
            group.labels.push(label);
        groups.set(maxBytes, group);
    }
    return [...groups.entries()]
        .sort(([left], [right]) => left - right)
        .map(([, group]) => `${group.labels.join(" · ")} up to ${formatArtifactMaxFileSize(group.sampleFileName, group.sampleMime)}`)
        .join("; ");
}
